import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { SIZES } from '../firebase/schema';

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    color: white;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.5rem;
    margin: 0 0 24px;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.8rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #62e07d;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #62e07d;
  }
`;

const Hint = styled.small`
  display: block;
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: none;
  font-weight: normal;
  font-size: 0.75rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
`;

const CancelButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;

  &:hover {
    background: #444;
  }
`;

const SaveButton = styled.button`
  background: #62e07d;
  color: black;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;

  &:hover {
    background: #50b366;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const emptyForm = {
  styleNo: '',
  brand: '',
  description: '',
  shortDescription: '',
  cogs: '',
  ssStyleId: '',
  sanmarStyleId: '',
  supplierUrl: '',
  defaultSizeGrid: '',
};

function productToForm(product) {
  if (!product) return { ...emptyForm };
  return {
    styleNo: product.styleNo || '',
    brand: product.brand || '',
    description: product.description || '',
    shortDescription: product.shortDescription || '',
    cogs: product.cogs ?? '',
    ssStyleId: product.ssStyleId || '',
    sanmarStyleId: product.sanmarStyleId || '',
    supplierUrl: product.supplierUrl || '',
    defaultSizeGrid: product.defaultSizeGrid || '',
  };
}

function EditProductModal({ product, onClose, onSaved, focusSupplierUrl = false }) {
  const [form, setForm] = useState(() => productToForm(product));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(productToForm(product));
  }, [product]);

  if (!product) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const productRef = doc(db, 'products', product.id);
      const payload = {
        styleNo: form.styleNo,
        brand: form.brand,
        description: form.description,
        shortDescription: form.shortDescription,
        cogs: parseFloat(form.cogs) || 0,
        ssStyleId: form.ssStyleId || '',
        sanmarStyleId: form.sanmarStyleId || '',
        supplierUrl: form.supplierUrl,
        defaultSizeGrid: form.defaultSizeGrid || null,
        updatedAt: new Date(),
      };
      await updateDoc(productRef, payload);
      onSaved?.({ id: product.id, ...product, ...payload });
      onClose?.();
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error updating product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h2>Edit Product</h2>
        <FormGroup>
          <Label>Style No *</Label>
          <Input value={form.styleNo} onChange={(e) => setForm({ ...form, styleNo: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label>Brand</Label>
          <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label>Supplier Description</Label>
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </FormGroup>
        <FormGroup>
          <Label>Short Description</Label>
          <Input
            value={form.shortDescription}
            onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
            placeholder="e.g., Raglan"
          />
        </FormGroup>
        <FormGroup>
          <Label>COGS</Label>
          <Input
            type="number"
            step="0.01"
            value={form.cogs}
            onChange={(e) => setForm({ ...form, cogs: e.target.value })}
          />
        </FormGroup>
        <FormGroup>
          <Label>S&amp;S Style ID</Label>
          <Input
            value={form.ssStyleId}
            onChange={(e) => setForm({ ...form, ssStyleId: e.target.value })}
            placeholder="e.g., 00760"
          />
        </FormGroup>
        <FormGroup>
          <Label>SanMar Style ID</Label>
          <Input
            value={form.sanmarStyleId}
            onChange={(e) => setForm({ ...form, sanmarStyleId: e.target.value })}
            placeholder="e.g., BC3001"
          />
        </FormGroup>
        <FormGroup>
          <Label>Supplier URL</Label>
          <Input
            type="url"
            autoFocus={focusSupplierUrl}
            value={form.supplierUrl}
            onChange={(e) => setForm({ ...form, supplierUrl: e.target.value })}
            placeholder="https://supplier.com/product"
          />
          <Hint>Paste the supplier page URL so ordering can open it with one click.</Hint>
        </FormGroup>
        <FormGroup>
          <Label>Default Size Grid</Label>
          <Select
            value={form.defaultSizeGrid}
            onChange={(e) => setForm({ ...form, defaultSizeGrid: e.target.value })}
          >
            <option value="">None (user chooses per line)</option>
            {Object.keys(SIZES).map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </Select>
        </FormGroup>
        <ButtonGroup>
          <CancelButton type="button" onClick={onClose}>
            Cancel
          </CancelButton>
          <SaveButton type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Product'}
          </SaveButton>
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
}

export default EditProductModal;
